import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  LinearProgress,
  Stack,
  SvgIcon,
  TextField,
  Theme,
  Typography,
  linearProgressClasses,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useAppSelector } from "hooks/use-app-select";
import { useEffect, useRef, useState } from "react";
import history from "utils/history";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import {
  checkPasswordStrength,
  handleErrorMessage,
  isPasswordStrong,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { selectAuthenticated } from "redux/authSlice";
import urls from "global/constants/UrlConstants";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useLocation } from "react-router-dom";
import { updatePwd, verifyTempPassword } from "./AuthService";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import strings from "global/constants/StringConstants";
import { CustomInput } from "global/components";
import { useAuthRouteRedirection } from "layouts/Config";
import viewpaths from "global/constants/ViewPathConstants";
import authLayoutStyles from "layouts/AuthLayout/AuthLayout.styles";
import authStyles from "./Auth.styles";
import pandaIcon from "assets/icons1/panda_icon.svg";
import mobilePandaIcon from "assets/icons1/mobile_panda_icon.svg";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#85C935",
  },
}));

interface CustomProps {
  location: any;
}

const NewPassword = (props: CustomProps) => {
  const classes = authStyles;
  useTitle(title.RESET_PASSWORD);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const context = new URLSearchParams(useLocation().search).get("context");
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [user, setUser] = useState<any>();
  const [credentials, setCredentials] = useState({
    new_password: {
      value: "",
      error: "",
    },
    confirm_password: {
      value: "",
      error: "",
    },
  });
  const [visibility, setVisibility] = useState({
    new_password: false,
    confirm_password: false,
  });
  const [processing, setProcessing] = useState<boolean>(false);
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  useEffect(() => {
    verifyPwd(context);
  }, []);

  const verifyPwd = async (token: any) => {
    try {
      const userData: any = {
        authToken: token,
      };
      const response: any = await verifyTempPassword(userData);
      setUser(response);
    } catch (error: any) {
      handleErrorMessage(error);
      history.push(viewpaths.landingViewPath);
    }
  };

  const handleValidation = () => {
    let errors = credentials;
    let isValid = true;
    const newPassword = credentials.new_password.value;
    const confirmPassword = credentials.confirm_password.value;
    if (!newPassword && !confirmPassword) {
      errors.new_password.error = "New password is required";
      errors.confirm_password.error = "Confirm password is required";
    }
    if (!newPassword) {
      errors.new_password.error = "New password is required";
      isValid = false;
    }
    if (!confirmPassword) {
      errors.confirm_password.error = "Confirm password is required";
      isValid = false;
    }
    if (newPassword !== confirmPassword) {
      openErrorNotification("New password and confirm password does not match");
      return false;
    }
    if (
      credentials.new_password.value &&
      !isPasswordStrong(credentials.new_password.value)
    ) {
      openErrorNotification("Entered password is not strong");
      isValid = false;
    }
    setCredentials({ ...errors });
    return isValid;
  };

  // const validate = () => {
  //   if (!isTruthy(credentials.new_password)) {
  //     openErrorNotification("Please enter the password");
  //     return false;
  //   }
  //   if (!isTruthy(credentials.new_password)) {
  //     openErrorNotification("Please enter the confirm password");
  //     return false;
  //   }
  //   if (credentials.new_password !== credentials.confirm_password) {
  //     openErrorNotification("New password and confirm password does not match");
  //     return false;
  //   }
  //   if (!isPasswordStrong(credentials.new_password)) {
  //     openErrorNotification("Entered password is not strong");
  //     return false;
  //   }
  //   return true;
  // };

  const makeNewPassword = async () => {
    try {
      if (handleValidation()) {
        let captchaToken: any;
        if (urls.PROD) {
          captchaToken = await executeRecaptcha!();
        }
        setProcessing(true);
        const payloadBody = {
          ...user,
          pwd: credentials.new_password.value,
          captchaToken: captchaToken,
        };
        const token = user.authToken;
        await updatePwd(payloadBody, token);
        openSuccessNotification("Your password has been changed successfully");
        history.push(viewpaths.landingViewPath);
        setProcessing(false);
      }
    } catch (error: any) {
      setProcessing(false);
      openErrorNotification(error.message);
    }
  };

  const handleChange = (event: any) => {
    setCredentials({
      ...credentials,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    });
  };

  const passwordStrength = () => {
    const { percentage, strength } = checkPasswordStrength(
      credentials.new_password.value
    );
    return (
      <Stack>
        <Typography
          variant="body2"
          color="text.primary"
        >{`Password Strength: ${strength}`}</Typography>
        <BorderLinearProgress variant="determinate" value={percentage} />
        <Typography mt={2} variant="body2" color="text.primary">
          Use at least 8 characters, one uppercase letter, one lowercase letter
          and one number in your password.
        </Typography>
      </Stack>
    );
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      makeNewPassword();
    }
  };

  const renderNewPasswordScreen = () => {
    return (
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={mdUp ? pandaIcon : mobilePandaIcon}
          alt="Panda"
          sx={{
            position: "absolute",
            width: mdUp ? "150px" : "100px",
            height: "auto",
            right: mdUp ? "-86px" : "50%",
            left: mdUp ? "auto" : "50%",
            top: mdUp ? "50%" : "-70px",
            transform: mdUp ? "translateY(-50%)" : "translateX(-50%)",
            zIndex: 2,
          }}
        />
        <Card elevation={16} sx={classes.cardBox}>
          <CardHeader
            subheader={
              <Typography
                color="text.primary"
                variant="body2"
                textAlign={"center"}
              >
                Choose a new password. Make sure it’s strong!
              </Typography>
            }
            sx={{ pb: 0 }}
            title={
              <Typography
                color="text.primary"
                variant="h4"
                textAlign={"center"}
                gutterBottom
              >
                Enter New Password
              </Typography>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <CustomInput
                required
                autoFocus
                label="New Password"
                placeHolder="New Password"
                name="new_password"
                onChange={handleChange}
                type={visibility.new_password ? "text" : "password"}
                value={credentials.new_password.value}
                disabled={processing}
                onKeyPress={handleKeypress}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        setVisibility({
                          ...visibility,
                          new_password: !visibility.new_password,
                        })
                      }
                    >
                      <SvgIcon>
                        {visibility.new_password ? (
                          <VisibilityOutlinedIcon />
                        ) : (
                          <VisibilityOffOutlinedIcon />
                        )}
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                error={
                  !isTruthy(credentials.new_password.value) &&
                  credentials.new_password.error
                }
                helperText={
                  !isTruthy(credentials.new_password.value)
                    ? credentials.new_password.error
                    : ""
                }
              />
              <CustomInput
                required
                label="Confirm Password"
                placeHolder="Confirm Password"
                name="confirm_password"
                onChange={handleChange}
                type={visibility.confirm_password ? "text" : "password"}
                value={credentials.confirm_password.value}
                onKeyPress={handleKeypress}
                disabled={processing}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        setVisibility({
                          ...visibility,
                          confirm_password: !visibility.confirm_password,
                        })
                      }
                    >
                      <SvgIcon>
                        {visibility.confirm_password ? (
                          <VisibilityOutlinedIcon />
                        ) : (
                          <VisibilityOffOutlinedIcon />
                        )}
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                error={
                  !isTruthy(credentials.confirm_password.value) &&
                  credentials.confirm_password.error
                }
                helperText={
                  !isTruthy(credentials.confirm_password.value)
                    ? credentials.confirm_password.error
                    : ""
                }
              />
            </Stack>
            <Button
              fullWidth
              size="medium"
              sx={{ mt: 2 }}
              type="submit"
              variant="contained"
              onClick={makeNewPassword}
              disabled={processing}
            >
              Submit
            </Button>
            <Box mt={2}>{passwordStrength()}</Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  } else {
    return <AuthLayout>{renderNewPasswordScreen()}</AuthLayout>;
  }
};

export default NewPassword;
